// The night behind the table: slow dust and a faint lattice of threes.
// Cheap on purpose: a few dozen points, drawn at most thirty times a second,
// still when the player asks for less motion. Colours: config/palette.ts.

import { PALETTE } from "../config/palette.ts"

export type Sky = { readonly pulse: (amount: number) => void }

type Mote = { x: number; y: number; readonly r: number; readonly v: number; readonly a: number; readonly p: number }

const rgba = ([r, g, b]: readonly [number, number, number], a: number): string => `rgba(${r}, ${g}, ${b}, ${a})`

export const startSky = (canvas: HTMLCanvasElement, reduced: boolean): Sky => {
  const ctx = canvas.getContext("2d")
  // a browser may refuse a drawing context; the game plays on without a sky
  if (!ctx) return { pulse: () => undefined }
  let w = 0
  let h = 0
  let motes: Mote[] = []
  let pulse = 0

  const resize = (): void => {
    const ratio = window.devicePixelRatio
    const dpr = Math.min(2, ratio > 0 ? ratio : 1)
    w = canvas.clientWidth
    h = canvas.clientHeight
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    motes = Array.from({ length: Math.round((w * h) / 22000) }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 1.2 + 0.2,
      v: Math.random() * 0.06 + 0.01,
      a: Math.random() * 0.5 + 0.1,
      p: Math.random() * Math.PI * 2,
    }))
  }

  // three nested triangles of points, turning very slowly
  const lattice = (t: number): void => {
    const R = Math.min(w, h) * 0.62
    ctx.save()
    ctx.translate(w * 0.5, h * 0.56)
    ctx.rotate(t * 0.000004)
    for (let k = 0; k < 3; k++) {
      const r = R * (0.34 + k * 0.33)
      ctx.beginPath()
      for (let i = 0; i <= 3; i++) {
        const a = (i / 3) * Math.PI * 2 - Math.PI / 2 + k * (Math.PI / 3)
        if (i === 0) ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r)
        else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r)
      }
      ctx.strokeStyle = rgba(PALETTE.sky.lattice, 0.035 + pulse * 0.05)
      ctx.lineWidth = 1
      ctx.stroke()
    }
    ctx.restore()
  }

  const drift = (m: Mote, t: number): void => {
    m.y -= m.v
    m.x += Math.sin(t * 0.0002 + m.p) * 0.05
    if (m.y >= -4) return
    m.y = h + 4
    m.x = Math.random() * w
  }

  let last = 0
  const frame = (t: number): void => {
    if (t - last > 33 || reduced) {
      last = t
      ctx.clearRect(0, 0, w, h)
      const g = ctx.createRadialGradient(w * 0.5, h * 0.42, 0, w * 0.5, h * 0.5, Math.max(w, h) * 0.8)
      g.addColorStop(0, PALETTE.sky.glowInner)
      g.addColorStop(0.55, PALETTE.sky.glowMid)
      g.addColorStop(1, PALETTE.sky.glowOuter)
      ctx.fillStyle = g
      ctx.fillRect(0, 0, w, h)
      lattice(t)
      for (const m of motes) {
        if (!reduced) drift(m, t)
        const twinkle = 0.6 + 0.4 * Math.sin(t * 0.001 + m.p)
        ctx.beginPath()
        ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2)
        ctx.fillStyle = rgba(PALETTE.sky.dust, m.a * twinkle)
        ctx.fill()
      }
      pulse *= 0.96
    }
    if (!reduced) requestAnimationFrame(frame)
  }

  resize()
  window.addEventListener("resize", () => {
    resize()
    if (reduced) frame(0)
  })
  requestAnimationFrame(frame)
  return {
    // a brief brightening, used when a fold lands
    pulse: (amount) => {
      pulse = Math.min(1, pulse + amount)
      if (reduced) frame(0)
    },
  }
}
